import { ArrowLeftRight, Plus, X } from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import {
  createFlashcardCollectionInputSchema,
  type CreateFlashcardsResponse,
  type FlashcardCollection,
} from "@learn-flow/contracts";

import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { createFlashcardCollection, createFlashcards } from "./flashcard-api.js";

type CardFormErrors = { question?: string; answer?: string; collectionId?: string };

export function FlashcardDialog({
  activityId,
  initialQuestion,
  collections,
  onCollectionCreated,
  onSaved,
  onUnauthorized,
  onClose,
}: {
  activityId: string | null;
  initialQuestion: string;
  collections: FlashcardCollection[];
  onCollectionCreated: (collection: FlashcardCollection) => void;
  onSaved: (result: CreateFlashcardsResponse) => void;
  onUnauthorized?: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const newCollectionRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);
  const headingId = useId();
  const questionId = useId();
  const answerId = useId();
  const notesId = useId();
  const collectionIdField = useId();
  const newCollectionId = useId();
  const questionErrorId = useId();
  const answerErrorId = useId();
  const collectionErrorId = useId();
  const [question, setQuestion] = useState(initialQuestion);
  const [answer, setAnswer] = useState("");
  const [notes, setNotes] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [createReverse, setCreateReverse] = useState(false);
  const [errors, setErrors] = useState<CardFormErrors>({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [createdCollections, setCreatedCollections] = useState<FlashcardCollection[]>([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [collectionError, setCollectionError] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [idempotencyKey] = useState(createUuid);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!dialog.open) dialog.showModal();
    questionRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const swap = () => {
    setQuestion(answer);
    setAnswer(question);
    setErrors({});
    setSaveError("");
  };

  const createCollection = async () => {
    const parsed = createFlashcardCollectionInputSchema.safeParse({ name: newCollectionName });
    if (!parsed.success) {
      setCollectionError(
        parsed.error.flatten().fieldErrors.name?.[0] ?? "Enter a collection name.",
      );
      newCollectionRef.current?.focus();
      return;
    }
    setCreatingCollection(true);
    setCollectionError("");
    try {
      const result = await createFlashcardCollection(parsed.data);
      setCreatedCollections((current) => [...current, result.collection]);
      onCollectionCreated(result.collection);
      setCollectionId(result.collection.id);
      setNewCollectionName("");
      setShowNewCollection(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        onUnauthorized?.();
        return;
      }
      setCollectionError(
        (error instanceof ApiError ? error.problem?.fieldErrors?.name?.[0] : undefined) ??
          (error instanceof ApiError && error.problem?.detail
            ? error.problem.detail
            : "The collection could not be created. Your name is still here; try again."),
      );
    } finally {
      setCreatingCollection(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;
    const nextErrors: CardFormErrors = {};
    if (!question.trim()) nextErrors.question = "Enter a question.";
    else if (question.trim().length > 10_000)
      nextErrors.question = "Keep each side of the flashcard under 10,000 characters.";
    if (!answer.trim()) nextErrors.answer = "Enter an answer.";
    else if (answer.trim().length > 10_000)
      nextErrors.answer = "Keep each side of the flashcard under 10,000 characters.";
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSaveError("");
      if (nextErrors.question) questionRef.current?.focus();
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    setErrors({});
    setSaveError("");
    try {
      const result = await createFlashcards(activityId, {
        question,
        answer,
        notes,
        collectionId: collectionId || null,
        createReverse,
        idempotencyKey,
      });
      onSaved(result);
    } catch (error) {
      submittingRef.current = false;
      setSaving(false);
      if (error instanceof ApiError && error.status === 401) {
        onUnauthorized?.();
        return;
      }
      const fieldErrors = error instanceof ApiError ? error.problem?.fieldErrors : undefined;
      setErrors({
        ...(fieldErrors?.question?.[0] ? { question: fieldErrors.question[0] } : {}),
        ...(fieldErrors?.answer?.[0] ? { answer: fieldErrors.answer[0] } : {}),
        ...(fieldErrors?.collectionId?.[0] ? { collectionId: fieldErrors.collectionId[0] } : {}),
      });
      setSaveError(
        error instanceof ApiError && error.problem?.detail
          ? error.problem.detail
          : "The flashcard was not saved. Your entries are still here; try again.",
      );
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="flashcard-dialog flashcard-create-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault();
        if (!saving && !creatingCollection) onClose();
      }}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef.current)}
    >
      <div className="flashcard-dialog-heading">
        <div>
          <span className="section-kicker">Flashcard</span>
          <h2 id={headingId}>Create new text flashcard</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Close flashcard form"
          disabled={saving || creatingCollection}
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <form onSubmit={(event) => void submit(event)} autoComplete="off" noValidate>
        <div className="flashcard-dialog-body">
          <div className="flashcard-field">
            <label htmlFor={questionId}>Question</label>
            <textarea
              id={questionId}
              ref={questionRef}
              value={question}
              rows={4}
              aria-invalid={Boolean(errors.question)}
              aria-describedby={errors.question ? questionErrorId : undefined}
              disabled={saving || creatingCollection}
              onChange={(event) => {
                setQuestion(event.target.value);
                setErrors((current) => withoutError(current, "question"));
                setSaveError("");
              }}
            />
            {errors.question ? (
              <span id={questionErrorId} className="field-error" role="alert">
                {errors.question}
              </span>
            ) : null}
          </div>

          <button
            type="button"
            className="flashcard-swap-button"
            disabled={saving || creatingCollection}
            onClick={swap}
          >
            <ArrowLeftRight size={15} aria-hidden="true" /> Swap question and answer
          </button>

          <div className="flashcard-field">
            <label htmlFor={answerId}>Answer</label>
            <textarea
              id={answerId}
              value={answer}
              rows={4}
              aria-invalid={Boolean(errors.answer)}
              aria-describedby={errors.answer ? answerErrorId : undefined}
              disabled={saving || creatingCollection}
              onChange={(event) => {
                setAnswer(event.target.value);
                setErrors((current) => withoutError(current, "answer"));
                setSaveError("");
              }}
            />
            {errors.answer ? (
              <span id={answerErrorId} className="field-error" role="alert">
                {errors.answer}
              </span>
            ) : null}
          </div>

          <div className="flashcard-field">
            <label htmlFor={notesId}>Notes</label>
            <textarea
              id={notesId}
              value={notes}
              rows={6}
              disabled={saving || creatingCollection}
              onChange={(event) => {
                setNotes(event.target.value);
                setSaveError("");
              }}
            />
          </div>

          {showNewCollection ? (
            <div className="flashcard-new-collection">
              <div className="flashcard-field">
                <label htmlFor={newCollectionId}>New collection name</label>
                <input
                  ref={newCollectionRef}
                  id={newCollectionId}
                  value={newCollectionName}
                  aria-invalid={Boolean(collectionError)}
                  aria-describedby={collectionError ? collectionErrorId : undefined}
                  disabled={saving || creatingCollection}
                  onChange={(event) => {
                    setNewCollectionName(event.target.value);
                    setCollectionError("");
                  }}
                />
              </div>
              <button
                type="button"
                className="soft-button"
                disabled={creatingCollection}
                onClick={() => void createCollection()}
              >
                {creatingCollection ? "Creating…" : "Create collection"}
              </button>
              {collectionError ? (
                <p id={collectionErrorId} className="field-error" role="alert">
                  {collectionError}
                </p>
              ) : null}
            </div>
          ) : null}

          {saveError ? (
            <p className="mutation-error" role="alert">
              {saveError}
            </p>
          ) : null}
          <p className="flashcard-create-progress" role="status" aria-live="polite">
            {saving ? "Saving flashcard…" : ""}
          </p>
        </div>
        <div className="flashcard-dialog-options">
          <div className="flashcard-collection-row">
            <div className="flashcard-field">
              <label htmlFor={collectionIdField}>Flashcard collection</label>
              <select
                id={collectionIdField}
                value={collectionId}
                aria-invalid={Boolean(errors.collectionId)}
                aria-describedby={errors.collectionId ? collectionErrorId : undefined}
                disabled={saving || creatingCollection}
                onChange={(event) => {
                  setCollectionId(event.target.value);
                  setErrors((current) => withoutError(current, "collectionId"));
                }}
              >
                <option value="">Uncollected</option>
                {[
                  ...collections,
                  ...createdCollections.filter(
                    (created) => !collections.some((collection) => collection.id === created.id),
                  ),
                ].map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
              </select>
              {errors.collectionId ? (
                <span id={collectionErrorId} className="field-error" role="alert">
                  {errors.collectionId}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="text-button"
              aria-expanded={showNewCollection}
              disabled={saving || creatingCollection}
              onClick={() => {
                setShowNewCollection((current) => !current);
                setCollectionError("");
              }}
            >
              <Plus size={14} aria-hidden="true" /> New collection
            </button>
          </div>

          <label className="flashcard-reverse-option">
            <input
              type="checkbox"
              checked={createReverse}
              disabled={saving || creatingCollection}
              onChange={(event) => setCreateReverse(event.target.checked)}
            />
            <span>Create the reverse flashcard too</span>
          </label>
        </div>
        <div className="flashcard-dialog-actions">
          <button
            type="button"
            className="soft-button"
            disabled={saving || creatingCollection}
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={saving || creatingCollection}
            onPointerDown={preservePointerActivation}
          >
            {saving ? "Saving…" : "Save flashcard"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function preservePointerActivation(event: ReactPointerEvent<HTMLButtonElement>): void {
  if (!event.isPrimary || event.button !== 0) return;
  if (typeof event.currentTarget.setPointerCapture === "function") {
    // Native input UI can recenter the dialog before release; keep that release on Save.
    event.currentTarget.setPointerCapture(event.pointerId);
  }
}

function trapDialogFocus(
  event: ReactKeyboardEvent<HTMLDialogElement>,
  dialog: HTMLDialogElement | null,
): void {
  if (event.key !== "Tab" || !dialog) return;
  const controls = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function withoutError(errors: CardFormErrors, field: keyof CardFormErrors): CardFormErrors {
  const next = { ...errors };
  delete next[field];
  return next;
}
